import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { PageContentShell } from "../../components/layout/PageContentShell";
import { PageHeader } from "../../components/Page/PageHeader";
import { StatusChip, TypeChip } from "../../components/StatusChip";
import { UserAvatar } from "../../components/UserAvatar";
import { TICKET_STATUSES, ticketService, type Ticket, type TicketStatus, type TicketType } from "../../services/ticketService";
import { canCreateTickets } from "../../utils/authorization";
import { displayName, formatDate, formatTime } from "../../utils/display";

const TYPES: TicketType[] = ["SUPPORT", "FEATURE", "CHANGE", "BUG"];
const STATUSES: TicketStatus[] = TICKET_STATUSES;

export function TicketsListPage({ roles, admin }: { roles: string[]; admin?: boolean }) {
  const { t, i18n } = useTranslation();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [type, setType] = useState<TicketType | "ALL">("ALL");
  const [status, setStatus] = useState<TicketStatus | "ALL">("ALL");

  useEffect(() => {
    void ticketService
      .list()
      .then(setTickets)
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tickets.filter((ticket) => {
      if (type !== "ALL" && ticket.type !== type) return false;
      if (status !== "ALL" && ticket.status !== status) return false;
      if (!q) return true;
      const haystack = [
        ticket.number,
        ticket.title,
        ticket.requesterEmail,
        ticket.requesterUsername,
        ticket.tenant,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [tickets, query, type, status]);

  return (
    <PageContentShell>
      <PageHeader
        title={t("tickets.title")}
        subtitle={t("tickets.listSubtitle")}
        actions={
          canCreateTickets(roles) ? (
            <Button component={Link} to="/tickets/new" variant="contained" startIcon={<AddCircleOutlineIcon />}>
              {t("tickets.new")}
            </Button>
          ) : null
        }
      />
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("tickets.search")}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>{t("tickets.type")}</InputLabel>
            <Select value={type} label={t("tickets.type")} onChange={(e) => setType(e.target.value as TicketType | "ALL")}>
              <MenuItem value="ALL">{t("tickets.filterAll")}</MenuItem>
              {TYPES.map((item) => (
                <MenuItem key={item} value={item}>
                  {t(`types.${item}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>{t("tickets.status")}</InputLabel>
            <Select
              value={status}
              label={t("tickets.status")}
              onChange={(e) => setStatus(e.target.value as TicketStatus | "ALL")}
            >
              <MenuItem value="ALL">{t("tickets.filterAll")}</MenuItem>
              {STATUSES.map((item) => (
                <MenuItem key={item} value={item}>
                  {t(`status.${item}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>
      <Paper sx={{ overflow: "hidden" }}>
        {filtered.length === 0 ? (
          <Typography sx={{ p: 3 }} color="text.secondary">
            {tickets.length === 0 ? t("tickets.empty") : t("tickets.noMatches")}
          </Typography>
        ) : (
          filtered.map((ticket, index) => {
            const name = displayName(ticket.requesterUsername, ticket.requesterEmail);
            const date = formatDate(ticket.createdDate, i18n.language);
            const time = formatTime(ticket.createdDate, i18n.language);
            return (
              <Box
                key={ticket.id}
                component={Link}
                to={admin ? `/admin/tickets/${ticket.id}` : `/tickets/${ticket.id}`}
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "flex-start",
                  p: 2,
                  textDecoration: "none",
                  color: "inherit",
                  borderBottom: index === filtered.length - 1 ? "none" : "1px solid",
                  borderColor: "divider",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <UserAvatar username={ticket.requesterUsername} email={ticket.requesterEmail} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                    <Typography color="primary" fontWeight={700}>
                      {ticket.number}
                    </Typography>
                    <Typography fontWeight={600} noWrap>
                      {ticket.title}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                    <TypeChip type={ticket.type} />
                    <StatusChip status={ticket.status} />
                  </Stack>
                  <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                    <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary">
                      <PersonOutlineIcon sx={{ fontSize: 16 }} />
                      <Typography variant="caption">{name}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary">
                      <ScheduleOutlinedIcon sx={{ fontSize: 16 }} />
                      <Typography variant="caption">
                        {date}
                        {time ? ` · ${time}` : ""}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              </Box>
            );
          })
        )}
      </Paper>
    </PageContentShell>
  );
}
